import { useTheme } from '@mui/material';
import {
    BuildOutlined as RepairIcon,
    StarRate as StarRateIcon,
    StarBorderOutlined as StarBorderOutlinedIcon,
    DirectionsCar as ServiceIcon,
} from '@mui/icons-material';
import Box from '@/components/ui/Box/Box';
import Card from '@/components/ui/Card/Card';
import Divider from '@/components/ui/Divider/Divider';
import Typography from '@/components/ui/Typography/Typography';
import type { Job } from '@/types/types';
import type { JobCardProps } from './IJobCard';
import { styles } from './styles';

const STATUS_BAR_DISPLAY: Record<string, string> = {
    Open: 'Open',
    Accepted: 'Accepted',
    'In Progress': 'In Progress',
    Completed: 'Completed',
    Closed: 'Closed',
    Declined: 'Declined',
    Canceled: 'Canceled',
};

const STATUS_BAR_CLASS: Record<string, string> = {
    Open: 'OPEN',
    Accepted: 'ACCEPTED',
    'In Progress': 'IN_PROGRESS',
    Completed: 'COMPLETED',
    Closed: 'CLOSED',
    Declined: 'DECLINED',
    Canceled: 'CANCELED',
};

const capitalize = (s: string | null | undefined) =>
    !s ? '' : s.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());

const buildYYMC = (job: Job): string => {
    const v = job.vehicle;
    if (!v) return '';
    const parts: string[] = [];
    if (v.year || v.make || v.model) {
        parts.push(
            `${v.year ?? ''} ${capitalize(v.make)} ${capitalize(v.model)}`.replace(
                /\s+/g,
                ' ',
            ).trim(),
        );
    }
    if (v.color) parts.push(capitalize(v.color));
    return parts.join(', ');
};

const formatCurrency = (n: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);

const hasAllRatings = (job: Job): boolean => {
    if (!job.reviews) return false;
    const reviews = job.reviews as Array<{ reviewed_company: { id: string } }>;
    if (!reviews.length) return false;
    const uniqueRepairerIds = Array.from(
        new Set(job.services.map((s) => s.repair_company?.id).filter(Boolean)),
    );
    return uniqueRepairerIds.every((repairerId) =>
        reviews.some((r) => r.reviewed_company.id === repairerId),
    );
};

const JobCard = ({ job, isRepairer, isActive, onClick }: JobCardProps) => {
    const theme = useTheme();
    const jobStatuses = Array.from(
        new Set(job.services.map((s) => STATUS_BAR_DISPLAY[s.service_status] ?? s.service_status)),
    ).map((status) => ({ status, statusStyle: STATUS_BAR_CLASS[status] ?? 'OPEN' }));

    const services = Array.from(
        new Set(job.services.map((s) => s.service_details?.service_type).filter(Boolean)),
    ).join(', ');

    const repairers = isRepairer
        ? ((job as Job & { supplier_company?: { name?: string } }).supplier_company?.name ?? '')
        : Array.from(
            new Set(
                job.services
                    .map((s) => s.repair_company?.name)
                    .filter((name): name is string => Boolean(name)),
            ),
        ).join(', ');

    const total = job.services.reduce((sum, s) => sum + (s.price ?? 0), 0);
    const timestamp = job.created_at ? new Date(job.created_at).toDateString() : '';
    const location = job.location?.nickname ?? job.location?.formatted_address ?? '';
    const showStar = hasAllRatings(job);

    return (
        <Card sx={styles.card(isActive)} onClick={() => onClick(job.id)}>
            <Box sx={styles.headerRow}>
                <Typography sx={styles.timestamp}>{timestamp}</Typography>
                <Typography sx={styles.total}>{formatCurrency(total)}</Typography>
            </Box>
            <Box>
                <Typography sx={styles.yymc}>{buildYYMC(job)}</Typography>
                <Typography sx={styles.details}>VIN: {(job.vehicle?.vin ?? '').toUpperCase()}</Typography>
                <Typography sx={styles.details}>Stock/RO: {job.vehicle?.stock_number ?? ''}</Typography>
                <Typography sx={styles.location}>{location}</Typography>
            </Box>
            <Divider sx={styles.divider} />
            <Box>
                <Box sx={styles.serviceRow}>
                    <ServiceIcon sx={{ fontSize: 18, color: theme.palette.grey[500] }} />
                    <Typography sx={styles.details}>{services}</Typography>
                </Box>
                <Box sx={styles.repairerRow}>
                    <RepairIcon sx={{ fontSize: 18, color: theme.palette.grey[500] }} />
                    <Typography sx={styles.details}>{repairers}</Typography>
                    {showStar ? (
                        <StarRateIcon sx={styles.starIcon(theme)} />
                    ) : (
                        <StarBorderOutlinedIcon sx={styles.starIcon(theme)} />
                    )}
                </Box>
            </Box>
            <Divider sx={styles.divider} />
            <Box sx={styles.statusContainer}>
                {jobStatuses.map((el) => (
                    <Card
                        key={el.status}
                        // @ts-expect-error custom card variants declared via module augmentation
                        variant={el.statusStyle}
                    >
                        {el.status}
                    </Card>
                ))}
            </Box>
        </Card>
    );
};

export default JobCard;
